/// <reference path="../../../../WebUI/typings/tsd.d.ts" />
import * as React from "react";
import * as ReactDOM from "react-dom";

import * as Rest from "../../Generated/RestClient";
import { resourceStrings, AutoResizeTextArea, FooterControl } from "../../../../Intercom.ReactUI/Views/index";
import { clientAppSettings } from "../../Models/ClientAppSettings";
import { IconFile, SubscriptionsKeys } from "./BotPage";
import { BotEditorRow } from "./BotEditorRow";
import { BotVersionEditor } from "../../Views/BotVersionEditor";
import { BotIconControl } from "../Views/BotIconControl";
import * as Editor from '../../Views/Shared/IBotEditor';
import { BlockRowInputTooltipDisposer, BlockRowInputLabel } from '../../Views/Shared/BlockRowInputLabel';
import { UserAgreementConsentFormFields } from '../../Views/UserAgreementConsentFormFields';
import { BotAppSettings, BotAppSettingsProps } from "./BotAppSettings";
import { BotUtils } from "../../Utils/BotUtils";
import isInFrame from "../../../../Intercom.ReactUI/Utils/InFrame";

export interface BotEditorProps extends React.Props<BotEditor> {
    initialBot: Rest.BotService;
    inCreateMode: boolean;
    isAdmin: boolean;
    botAppSettingsProps: BotAppSettingsProps;
    onDropIcon: (files: IconFile[]) => void;
    isFeatureFlighted: (feature: string) => boolean;
    onSystemTagsChange(tags: string): void;
    onClickButton: React.EventHandler<React.MouseEvent<any>>;
    onSetEditor: (editor: BotEditor) => void;
    onSetBotAppSettings(botAppSettings: BotAppSettings);
}

export interface BotEditorState {
    bot?: Rest.BotService;
    termAcknowledged?: boolean;
    iconFile?: IconFile;
    subscriptionKeys?: SubscriptionsKeys;
    errors?: Editor.ErrorDict;
    isBusy?: boolean;
    previousTooltipDisposer?: BlockRowInputTooltipDisposer;
}

export class BotEditor extends React.Component<BotEditorProps, BotEditorState> implements Editor.IBotEditor {
    public static createNewState(bot: Rest.BotService): BotEditorState {
        return {
            bot: bot,
            errors: {}
        };
    }

    constructor(props: BotEditorProps) {
        super(props);

        this.state = BotEditor.createNewState(props.initialBot);
    }

    private onTooltipVisiblityChanged = (visible: boolean, disposer: BlockRowInputTooltipDisposer) => {
        if (this.state.previousTooltipDisposer === disposer) {
            return;
        }

        // If it's a different element call disposer for old one first
        if (this.state.previousTooltipDisposer) {
            this.state.previousTooltipDisposer();
        }

        this.setState({previousTooltipDisposer: disposer});
    };

    private handleArrayIndexedObjectChange(fieldName: string, index: number, childFieldName: string, ev: React.ChangeEvent<HTMLInputElement>): void {
        this.state.bot[fieldName][index][childFieldName] = ev.target.value;
        if (ev.target.value) {
            delete this.state.errors['#' + ev.target.id];
        }
        this.setState(this.state);
    }

    private handleCheckboxChange(fieldName: string, ev: React.ChangeEvent<HTMLInputElement>): void {
        this.state.bot[fieldName] = ev.target.checked;
        this.setState(this.state);
    }

    public handleChange = (fieldName: string, ev) => {
        this.state.bot[fieldName] = ev.target.value;
        if (ev.target.value) {
            delete this.state.errors['#' + ev.target.id];
        }
        this.setState(this.state);
    }

    public handleArrayChange = (fieldName: string, ev) => {
        this.state.bot[fieldName] = ev.target.value.split(',');
        if (ev.target.value) {
            delete this.state.errors['#' + ev.target.id];
        }
        this.setState(this.state);
    }

    componentDidMount() {
        $('#page-content').scrollTop(0);
    }

    public render(): JSX.Element {
        var buttons = this.renderButtons();
        const header: JSX.Element = this.props.inCreateMode ? <h1>Tell us about your bot</h1> : undefined;
        return (
            <div className="agent-page page-grid">
                <div className="bot-editor">
                    {header}
                    {this.renderFields()}
                    {buttons}
                </div>
                <FooterControl />
            </div>
        );
    }

    private hasVersionEndpoint(version: string) {
        for (var i = 0; i < this.state.bot.Endpoints.length; i++) {
            if (this.state.bot.Endpoints[i].Version === version) {
                return true;
            }
        }
        return false;
    }

    private renderAppInsightsDashboardFields(x: Rest.BotService, reflectedName: any): JSX.Element {
        return (
            <div>
                <BotEditorRow
                    label="Application Insights API key"
                    editor={this}
                    inputId={reflectedName(x.DeveloperAppInsightsAPIKey = null)}
                    inputTip="Provide an Azure Application Insights API Key if you want to view analytics about your bot in the Dashboard. <a href='//aka.ms/bf-appinsights-settings#api-key' target='_blank'>Learn more</a>"
                    tipAsHtml={true}
                    inputPlaceholder="API key (User-Generated Application Insights API key)"
                    onTooltipVisibilityChanged={this.onTooltipVisiblityChanged}
                    password={true} />

                <BotEditorRow
                    label="Application Insights Application ID"
                    editor={this}
                    inputId={reflectedName(x.DeveloperAppInsightsApplicationId = null)}
                    inputTip="Provide your Azure Application Insights Application ID if you want to view analytics about your bot in the Dashboard. <a href='//aka.ms/bf-appinsights-settings#application-id' target='_blank'>Learn more</a>"
                    tipAsHtml={true}
                    inputPlaceholder="Application ID (Application Insights Application ID)"
                    onTooltipVisibilityChanged={this.onTooltipVisiblityChanged} />
            </div>);
    }

    private renderFields(): JSX.Element {
        var x = {} as Rest.BotService;

        function reflectedName(value: any) {
            for (var key in x) {
                delete x[key];
                return key;
            }
            return null;
        }

        // get errors
        var agentDescError = this.state.errors[reflectedName(x.Description = null)];

        // set up error classes
        const errorClass = " input-in-error";
        var agentDescErrorClass = agentDescError ? errorClass : '';

        var behavior: JSX.Element;
        if (this.hasVersionEndpoint('1.0')) {
            var behaviorDisabled = (this.state.bot.Version !== '1.0');
            var behaviorNote = this.state.bot.Endpoints.length > 1 ? <small>Version 1.0 only</small> : null;
            var behavior = (
                <div className={"grid-card-base bot-card editor-card" + (behaviorDisabled ? ' disabled' : '')} disabled={behaviorDisabled}>
                    <h3>Behavior {behaviorNote}</h3>
                    <div className="detail-row">
                        <div className="checkbox-rect">
                            <input disabled={true} type="checkbox" id="allMessages" checked={false} />
                        </div>
                        <div className="checkbox-rhs">
                            <label htmlFor="allMessages">Listen to all messages</label>
                            <div className="checkbox-desc">Even if your bot isn't addressed directly, it will be able to listen and respond to all messages in the conversation.</div>
                        </div>
                    </div>
                    <div className="detail-row">
                        <div className="checkbox-rect">
                            <input disabled={true} type="checkbox" id="autoTranslate" checked={false} />
                        </div>
                        <div className="checkbox-rhs">
                            <label htmlFor="autoTranslate">Translate channel messages</label>
                            <div className="checkbox-desc">Translation provided by Bing Translate (privacy at <a href="http://aka.ms/translatorprivacy" target="_blank">http://aka.ms/translatorprivacy</a>) and requires
                                an additional update to your privacy statement. See the <a href="//aka.ms/bf-conduct" target="_blank">Bot Framework Developer Code of Conduct</a> for details.</div>
                        </div>
                    </div>
                </div>
            );
        }

        const azureSettings = this.renderAzureSettings();

        return (
            <div>
                {
                    azureSettings ?
                        <div className="grid-card-base bot-card editor-card">
                            {azureSettings}
                        </div>
                        : null
                }
                <div id="agent-details-card" className="grid-card-base bot-card editor-card">
                    <h3 id="bot-profile">Bot profile</h3>
                    <BotIconControl onDropIcon={this.props.onDropIcon} bot={this.state.bot} />
                    <BotEditorRow
                        editor={this}
                        inputId={reflectedName(x.Name = null)}
                        inputMaxlen={Editor.MAX_NAME_LEN}
                        inputMinlen={Editor.MIN_NAME_LEN}
                        inputPlaceholder="Name"
                        label="Display name"
                        required={true}
                        onTooltipVisibilityChanged={this.onTooltipVisiblityChanged}
                        inputTip={`Your bot's name in channels and directories; you can change this later. ${Editor.MAX_NAME_LEN} character limit.`} />

                    <BotEditorRow
                        editor={this}
                        onTooltipVisibilityChanged={this.onTooltipVisiblityChanged}
                        inputId={reflectedName(x.Id = null)}
                        required={true}
                        inputMaxlen={Editor.MAX_ID_LEN}
                        inputMinlen={Editor.MIN_ID_LEN}
                        inputReadonly={!this.props.inCreateMode}
                        label={resourceStrings.botId}
                        inputPlaceholder={`Type in your ${resourceStrings.botId}`}
                        inputTip={`Used in the URL for your bot. Alphanumeric and underscore only. Cannot be changed once registered.`} />

                    <div className="detail-row">
                        <BlockRowInputLabel
                            for="input-Description"
                            text="Long description"
                            required={false}
                            tip="Your bot's long description which may appear in channels or directories."
                            onTooltipVisibilityChanged={this.onTooltipVisiblityChanged} />
                        <AutoResizeTextArea className={agentDescErrorClass + ' description'}
                            placeHolder="Description"
                            id="input-Description"
                            value={this.state.bot.Description}
                            maxLength={Editor.MAX_DESC_LEN}
                            onChange={ev => this.handleChange('Description', ev)} />
                        <div key="desc1" className="error-info">{agentDescError}</div>
                    </div>

                </div>

                <div id="agent-details-card" className="grid-card-base bot-card editor-card">
                    <h3>Configuration</h3>
                    <BotVersionEditor
                        bot={this.state.bot}
                        inCreateMode={this.props.inCreateMode}
                        handleChange={(fieldName: string, ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => this.handleChange(fieldName, ev)}
                        handleArrayIndexedObjectChange={(fieldName: string, index: number, childFieldName: string, ev: React.ChangeEvent<HTMLInputElement>) => this.handleArrayIndexedObjectChange(fieldName, index, childFieldName, ev)}
                        handleCheckboxChange = {(fieldName: string, ev: React.ChangeEvent<HTMLInputElement>) => this.handleCheckboxChange(fieldName, ev)}
                        v1Secret={this.state.subscriptionKeys ? this.state.subscriptionKeys.primary : ''}
                        isAppTypeFlighted={this.props.isFeatureFlighted('appTypeEnabled')}
                        errors={this.state.errors} />
                </div>
                {behavior}

                <div className="config-info grid-card-base bot-card editor-card">
                    <h3>Analytics</h3>
                    <p>Enable Analytics for your bot via Azure Application Insights.&nbsp;
                        <a href='//aka.ms/bf-analytics' target='_blank'>Learn more.</a>
                    </p>
                    <BotEditorRow
                        label="Application Insights Instrumentation key"
                        editor={this}
                        inputId={reflectedName(x.DeveloperAppInsightKey = null) }
                        inputTip="Provide your Azure Application Insights Key if you want to receive bot telemetry. <a href='//aka.ms/bf-appinsights-settings#api-key' target='_blank'>Learn more</a>"
                        tipAsHtml={true}
                        inputPlaceholder="Instrumentation key (Azure Application Insights key)"
                        onTooltipVisibilityChanged={this.onTooltipVisiblityChanged} />

                    {this.renderAppInsightsDashboardFields(x, reflectedName) }
                </div>

                <div className="config-info grid-card-base bot-card editor-card">
                    <h3>Admin</h3>

                    <BotEditorRow
                        label="Owners"
                        editor={this}
                        inputId={reflectedName(x.OwnerIds = null)}
                        inputIsArray={true}
                        inputPlaceholder="Type email addresses separated by commas"
                        inputTip="Email(s) of people who have permission to edit this bot, comma or semicolon delimited."
                        onTooltipVisibilityChanged={this.onTooltipVisiblityChanged} />

                </div>

                {this.renderAdminTile() }
            </div>
        );
    }

    private renderAzureSettings(): JSX.Element {
        return (this.props.botAppSettingsProps ?
            <BotAppSettings {...this.props.botAppSettingsProps} ref={this.props.onSetBotAppSettings}/>
            : null);
    }

    private renderAdminTile(): JSX.Element {
        if (!this.props.isAdmin) {
            return null;
        }
        return (
            <div className="grid-card-base bot-card editor-card">
                <h3>BF Admin</h3>
                {this.renderBlockedRow()}
                {this.renderSystemTagsEditor()}
            </div>
        );
    }

    /**
     * TODO: move all system tags (featured, blocked, approved, rejected, feature display order, etc)
     * editing rendering to a derived admin editor page and move the code to AdminClient to avoid info disclosure.
     */
    private renderSystemTagAsCheckbox(tag: string, prompt: string): JSX.Element {
        let ref = `checkbox_${tag}`;
        let id = `agent_${tag}`;
        let checked = this.state.bot.SystemHashtags && this.state.bot.SystemHashtags.indexOf(tag) >= 0;

        return (
            <div className="detail-row">
                <label className="label-above"></label>
                <div className="checkbox-rect">
                    <input ref={ref} type="checkbox" id={id}
                        checked={checked} onChange={e => this.syncSystemTag(e, tag, ref)} />
                </div>
                <label htmlFor={id}>{prompt}</label>
            </div>
        );
    }

    private renderBlockedRow(): JSX.Element {
        return this.renderSystemTagAsCheckbox('blocked', 'Block bot in ' + resourceStrings.botDirectory + ' and switch');
    }

    private onSystemTagsChange(x: React.FormEvent<any>) {
        let text = x.currentTarget['value'];
        let tags = text.split(',').map(x => x.trim());
        this.props.onSystemTagsChange(tags);
    }

    private renderSystemTagsEditor(): JSX.Element {
        return (
            <div className="detail-row">
                <label className="label-above">System tags</label>
                <input ref="systemTags" type="text" id="agent-systemHashtags" value={this.getSystemTags()}
                    onChange={x => this.onSystemTagsChange(x)} />
            </div>
        );
    }

    private syncSystemTag(e: React.FormEvent<any>, tag: string, ref: string) {
        var $tags = $(ReactDOM.findDOMNode(this.refs['systemTags']));
        var $checkbox = $(ReactDOM.findDOMNode(this.refs[ref]));
        var on = !!$checkbox.is(':checked');
        var tags = $tags.val().split(',').filter(t => t.length);
        if (on) {
            tags.push(tag);
        } else {
            tags = tags.filter(t => t != tag);
        }
        this.props.onSystemTagsChange(tags);
    }

    private getSystemTags(): string {
        return this.state.bot.SystemHashtags ? this.state.bot.SystemHashtags.join(',') : '';
    }

    private onAgreementChanged = (enabled: boolean) => {
        let mutatedState = _.extend({}, this.state) as BotEditorState;
        if (mutatedState) {
            mutatedState.termAcknowledged = enabled;
        }

        this.setState(mutatedState);
    }

    private renderButtons(): JSX.Element {
        //
        // Do not remove cancel-button/register-button/delete-button from the classnames below unless you update BotEditController::onButtonClicked
        //
        var cancelButton = <button type="button" className="m-button-secondary-md cancel-button" onClick={this.props.onClickButton}>Cancel</button >;

        if (this.props.inCreateMode) {

            return (
                <div className="button-panel create-mode">
                    <div className="register-agreement">
                        <UserAgreementConsentFormFields
                            onAgreeToTermsChanged={this.onAgreementChanged} />
                    </div>
                    <button
                        className="m-button-primary-md focus-button register-button"
                        disabled={ this.state.isBusy || !this.state.termAcknowledged }
                        onClick={this.props.onClickButton}
                        type="button"
                    >
                        { resourceStrings.register }
                    </button>
                    {cancelButton}
                </div>
            );
        }
        else {
            var deleteButton = null as JSX.Element;
            var actionButton = null as JSX.Element;

            deleteButton = (
                isInFrame() ? null :
                (<button type="button" className="m-button-destructive-outline-md focus-button delete-button" disabled={this.state.isBusy} onClick={this.props.onClickButton}>{resourceStrings.deleteBot}</button>)
            );

            actionButton = (
                <button type="button" className="m-button-primary-md save-button" disabled={this.state.isBusy} onClick={this.props.onClickButton}>Save changes</button>
            );

            return (
                <div className="editor-card">
                    <div className="button-panel edit-mode">
                        <div className="left">
                            {cancelButton}
                            {actionButton}
                        </div>
                        <div className="right">
                            {deleteButton}
                        </div>
                    </div>
                </div>
            );
        }
    }
}




// WEBPACK FOOTER //
// ./Client/v4/Views/BotEditor.tsx